/**
 * FireBar — a rotating bar of fire orbs (Brief 012 Phase 2).
 *
 * THE determinism gotcha of this brief: a bar of segments rotating around a pivot
 * cannot compute its angle with Math.sin/cos (ESLint determinism guard). The fix is a
 * precomputed integer-step position LUT (engine/circleLut.ts): every segment advances
 * the SAME integer angle index per fixed step and reads its [dx, dy] offset from the
 * table — zero runtime trig. After exactly `speed` (steps/revolution) steps the index
 * returns to its start, so a replay reproduces the bar bit-for-bit.
 *
 * Structure (no render-loop change): the placed FIREBAR is an INVISIBLE pivot that
 * yields its N {@link FireBarSegment} orbs via {@link Entity.spawnChildren}. World adds
 * each orb to the live entity list, so every orb is a normal single-sprite always-hurt
 * hazard — the generic render loop and overlap pass handle them with no special case.
 * On respawn the pivot is reconstructed → fresh orbs at index 0 → deterministic restart.
 *
 * Collision is per-ORB AABB (each orb is its own entity), exactly the brief's "hurt
 * within any segment's AABB". ESLint-clean (Math.floor + multiply/mod only).
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { circleIndex, circleOffset } from '../circleLut';

/** Fire-orb hitbox (a touch smaller than a cell). */
const ORB_HALF = 20;
const DEFAULT_SEGMENTS = 4;
const DEFAULT_LENGTH_TILES = 1; // radius step between consecutive orbs
const DEFAULT_STEPS_PER_REV = 192; // ~3.2s/rev @ 60Hz — a classic slow fire bar
const MIN_STEPS_PER_REV = 8;
const MAX_SEGMENTS = 16;

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/**
 * One orbiting fire orb. Holds its pivot, its radius, and the shared revolution period;
 * advances its own integer step counter each fixed step. Because every orb of a bar
 * advances identically from step 0, they stay in lockstep with no shared mutable state.
 */
export class FireBarSegment implements Entity {
  readonly type = EntityType.FIREBAR;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private step = 0;

  constructor(
    private readonly pivotX: number,
    private readonly pivotY: number,
    private readonly radius: number,
    private readonly stepsPerRev: number,
    private readonly dir: 1 | -1,
  ) {
    this.body = { x: pivotX - ORB_HALF, y: pivotY - ORB_HALF, w: ORB_HALF * 2, h: ORB_HALF * 2 };
    this.place(); // seat at index 0 before the first step
  }

  private place(): void {
    const [ox, oy] = circleOffset(circleIndex(this.step, this.stepsPerRev, this.dir));
    const cx = this.pivotX + this.radius * ox;
    const cy = this.pivotY + this.radius * oy;
    this.body.x = cx - this.body.w / 2;
    this.body.y = cy - this.body.h / 2;
  }

  update(_dt: number, _ctx: EntityContext): void {
    this.step++;
    this.place();
  }

  onPlayerOverlap(ctx: EntityContext): void {
    ctx.hurtPlayer(); // always-hurt; never stompable
  }

  spriteKey(): string { return 'fireball'; }
  visible(): boolean { return true; }
}

/**
 * The placed fire-bar entity: an invisible, non-colliding pivot that simply emits its
 * orbiting segments. It never hurts or renders — the orbs do all the work.
 */
export class FireBar implements Entity {
  readonly type = EntityType.FIREBAR;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly segs: FireBarSegment[];

  /**
   * @param cx    placed-cell center-x, @param feetY placed-cell bottom.
   * @param props `segments` (orb count, def 4, clamped 1..16), `length` (radius step in
   *              tiles between orbs, def 1), `speed` (steps per revolution, def 192,
   *              min 8 — bigger = slower), `dir` (+1 cw / -1 ccw, def +1).
   * @param ts    tile size (px). The pivot sits at the placed cell's CENTER.
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const pivotX = cx;
    const pivotY = feetY - ts / 2; // cell center
    const segments = Math.max(1, Math.min(MAX_SEGMENTS, Math.floor(numProp(props, 'segments', DEFAULT_SEGMENTS))));
    const segLen = numProp(props, 'length', DEFAULT_LENGTH_TILES) * ts;
    const stepsPerRev = Math.max(MIN_STEPS_PER_REV, Math.floor(numProp(props, 'speed', DEFAULT_STEPS_PER_REV)));
    const dir: 1 | -1 = numProp(props, 'dir', 1) < 0 ? -1 : 1;

    this.segs = [];
    for (let k = 1; k <= segments; k++) {
      this.segs.push(new FireBarSegment(pivotX, pivotY, k * segLen, stepsPerRev, dir));
    }
    // Tiny pivot body; never hurts (onPlayerOverlap is a no-op) and never draws.
    this.body = { x: pivotX - 4, y: pivotY - 4, w: 8, h: 8 };
  }

  /** The orbs render/collide; this pivot expands into them at build (Brief 012). */
  spawnChildren(): Entity[] { return this.segs; }

  update(_dt: number, _ctx: EntityContext): void { /* pivot is inert; orbs tick themselves */ }
  onPlayerOverlap(_ctx: EntityContext): void { /* pivot never hurts */ }

  spriteKey(): string { return 'fireball'; }
  visible(): boolean { return false; } // invisible pivot
}
